/**
 * Admin Dashboard - Main overview with real-time statistics
 */

import React, { useState, useEffect } from 'react';
import { AdminService, DashboardStats, ActivityItem, SystemHealth } from '../../services/admin';
import { MetricsCard, UserMetricsCard, RunMetricsCard } from '../../components/admin/MetricsCard';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { SystemHealthWidget } from '../../components/admin/SystemHealthWidget';
import { OrphanedDroplets } from '../../components/admin/OrphanedDroplets';

export function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
    
    // Refresh every 30 seconds
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setError(null);
      
      // Load all dashboard data in parallel
      const [statsResponse, activityResponse, healthResponse] = await Promise.all([
        AdminService.getDashboardStats(),
        AdminService.getRecentActivity(10), // Get last 10 activities
        AdminService.getSystemHealth()
      ]);
      
      setStats(statsResponse);
      setActivity(activityResponse || []); // Ensure activity is always an array
      setHealth(healthResponse);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
      // Set safe defaults
      setActivity([]);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getActionDisplayName = (action: string) => {
    return action.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getResourceIcon = (resourceType: string) => {
    switch (resourceType) {
      case 'user': return '👤';
      case 'lab': return '🧪';
      case 'run': return '🚀';
      case 'grade': return '📊';
      case 'system': return '⚙️';
      default: return '📄';
    }
  };

  if (loading) {
    return (
      <div className="p-8 bg-gray-900 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-white mb-8">Admin Dashboard</h1>
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-gray-400 mt-1">
              System overview and management console
            </p>
          </div>
          <div className="text-sm text-gray-400">
            Last updated: {stats ? formatTimestamp(stats.timestamp) : '--'}
          </div>
        </div>

        {error && (
          <Alert
            variant="error"
            title="Error"
            className="mb-6"
          >
            {error}
          </Alert>
        )}

        {/* System Health Alert */}
        {health && health.overall !== 'healthy' && (
          <Alert
            variant="warning"
            title="System Health Warning"
            className="mb-6"
          >
            System status: {health.overall}. Check system monitoring for details.
          </Alert>
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats && (
            <>
              <UserMetricsCard stats={stats.users} loading={!stats} />
              <RunMetricsCard stats={stats.runs} loading={!stats} />
              <MetricsCard
                title="Labs Available"
                value={stats.labs.total_labs}
                subtitle={`${Object.values(stats.labs.labs_by_difficulty).reduce((a, b) => a + b, 0)} total`}
                icon="🧪"
              />
              <MetricsCard
                title="Activity (24h)"
                value={stats.recent.new_runs_24h}
                subtitle={`${stats.recent.new_users_24h} new users`}
                icon="📈"
              />
            </>
          )}
        </div>

        {/* System Health Monitoring */}
        <div className="mt-6">
          <SystemHealthWidget />
        </div>

        {/* Orphaned Droplets Management */}
        <div className="mt-6">
          <OrphanedDroplets />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-6">
          {/* Recent Activity Feed */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Recent Activity</h3>
              <button 
                onClick={loadDashboardData}
                className="text-blue-400 hover:text-blue-300 text-sm"
              >
                Refresh
              </button>
            </div>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {activity && activity.length > 0 ? (
                activity.map((item) => (
                  <div key={item.id} className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg">
                    <span className="text-lg">
                      {getResourceIcon(item.resource_type)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-medium text-sm">
                          {getActionDisplayName(item.action)}
                        </span>
                        <span className="text-gray-400 text-xs">
                          {item.resource_type}
                        </span>
                      </div>
                      <div className="text-gray-400 text-xs mb-1">
                        {item.user ? (
                          <span>
                            by <span className="text-blue-400">{item.user.username}</span>
                            <span className="text-gray-500"> ({item.user.role})</span>
                          </span>
                        ) : (
                          'System action'
                        )}
                      </div>
                      <div className="text-gray-500 text-xs">
                        {formatTimestamp(item.created_at)}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-400">
                  No recent activity
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Quick Stats Summary */}
        {stats && (
          <div className="mt-8">
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Lab Difficulty Distribution</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(stats.labs.labs_by_difficulty).map(([difficulty, count]) => (
                  <div key={difficulty} className="text-center p-4 bg-gray-800 rounded-lg">
                    <div className="text-2xl font-bold text-white mb-1">
                      {count}
                    </div>
                    <div className="text-sm text-gray-400 capitalize">
                      {difficulty} Labs
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}